import {
  AllStructuresForHomePageQuery,
  useAllStructuresForHomePageQuery,
} from '@/__generated__/graphql';
import Grid from '@mui/material/Grid2';
import { useFeatureFlag, useRouter } from '@/hooks';
import * as state from '@/state/atoms';
import { abyssColors } from '@/theme/colors';
import { getStructureRedirectPath } from '@/utils/structureRedirectPath';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Box, Button, Stack, Tabs, Typography, Tab } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAtomValue } from 'jotai';
import { AbyssIcon } from '@/assets/icons/AbyssIcon';
import { XlsxProcessor } from '@/utils/xlsx';
import { PageLoader } from '../shared/PageLoader';
import { DeploymentCard } from './DeploymentCard';
import { PaintBockAndDeckGraphsContainer } from './PaintBlockAndDeckGraphsContainer/PaintBlockAndDeckGraphsCotainer';
import { AssetsContainer, MainContainer } from './styles';
import { TabContent } from '../Analysis/modules/pointOfInterest/PointOfInterestTab/SelectedPoiContent';
import { TemporalAnalysisGraphsContainer } from './TemporalAnalysisGraphsContainer/TemporalAnalysisGraphsContainer';

export const Assets = () => {
  const router = useRouter();
  const { platformId } = router.query;
  const platformName = useAtomValue(state.platformName);
  const isBetaUser = useFeatureFlag('beta-user');
  const isGenericEngineer = useFeatureFlag('generic-engineer');

  const [paintBlockTableXlsxProcessor, setPaintBlockTableXlsxProcessor] = useState<
    XlsxProcessor | undefined
  >(undefined);

  const { loading, data } = useAllStructuresForHomePageQuery({
    variables: {
      input: {
        platformId: platformId?.toString(),
      },
    },
  });

  const [activeDeployments, setActiveDeployments] = useState<
    AllStructuresForHomePageQuery['allStructures'] | undefined
  >([]);
  const [previousDeployments, setPreviousDeployments] = useState<
    AllStructuresForHomePageQuery['allStructures'] | undefined
  >([]);

  useEffect(() => {
    const { allStructures } = data || {};
    if (allStructures?.length === 1) {
      const { id, status } = allStructures[0];
      const pathname = getStructureRedirectPath({ id, status }, isBetaUser);
      router.push({
        pathname,
      });
    }
    setActiveDeployments(allStructures?.filter((item) => item.isActive));
    setPreviousDeployments(allStructures?.filter((item) => !item.isActive));
  }, [loading, data, router, isBetaUser]);

  const downloadXlsxFile = useCallback(async () => {
    if (paintBlockTableXlsxProcessor) {
      const fileName = `${platformName} ${new Date().getFullYear()} - Aggregate Ri Values.xlsx`;
      paintBlockTableXlsxProcessor.download(fileName);
    }
  }, [paintBlockTableXlsxProcessor, platformName]);

  const deploymentIds = useMemo(() => {
    return activeDeployments ? activeDeployments?.map((item) => item.id) : [];
  }, [activeDeployments]);

  const tabsData = useMemo(() => {
    const baseTabs = [
      {
        id: 0,
        label: 'High level insights',
        component: (
          <PaintBockAndDeckGraphsContainer
            activeDeploymentIds={deploymentIds}
            setPaintBlockTableXlsxProcessor={setPaintBlockTableXlsxProcessor}
            activeDeployments={activeDeployments || []}
          />
        ),
      },
    ];

    if (!isGenericEngineer) {
      baseTabs.push({
        id: 1,
        label: 'Temporal analysis',
        component: <TemporalAnalysisGraphsContainer activeDeploymentIds={deploymentIds} />,
      });
    }

    return baseTabs;
  }, [deploymentIds, isGenericEngineer, activeDeployments]);

  const [selectedTabId, setSelectedTabId] = useState(tabsData[0].id);

  const handleTabsChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedTabId(newValue);
  };

  return loading || (data?.allStructures && data.allStructures.length === 1) ? (
    <Box sx={{ position: 'relative', top: '25rem' }}>
      <PageLoader />
    </Box>
  ) : (
    <MainContainer>
      <AssetsContainer>
        <Stack direction="row" alignItems="center" gap={1}>
          <ArrowBackIcon onClick={() => router.back()} style={{ cursor: 'pointer' }} />
          <Typography variant="h5" fontWeight={700} color={abyssColors.primary[700]}>
            {platformName}
          </Typography>
        </Stack>

        <Typography variant="body2" color={abyssColors.primary[400]}>
          Select a deployment for more insights
        </Typography>

        <Typography
          variant="body2"
          fontSize={14}
          fontWeight={500}
          mt={3}
          color={abyssColors.primary[500]}
          mb={2}
        >
          Active Deployments
        </Typography>
        <Grid container rowSpacing={2} columnSpacing={2}>
          {activeDeployments?.map((item, index) => (
            <DeploymentCard
              key={`${item.id}-${index}`}
              deploymentStatus="active"
              structure={item}
            />
          ))}
        </Grid>

        <Stack direction="row" width="100%" justifyContent="space-between" alignItems="center">
          <Typography
            variant="body2"
            fontWeight={500}
            fontSize={14}
            mt={3}
            color={abyssColors.primary[500]}
            mb={2}
          >
            Previous Deployments
          </Typography>
          {activeDeployments && activeDeployments?.length > 0 && (
            <Button
              onClick={downloadXlsxFile}
              sx={{
                color: abyssColors.primary[500],
                padding: '0.4rem 1rem',
                height: 'auto',
                gap: '1rem',
              }}
            >
              <AbyssIcon iconName="file-download-line" />
              <Typography
                variant="body2"
                fontWeight={500}
                fontSize="1.3rem"
                lineHeight="normal"
                textTransform="none"
              >
                Export Excel
              </Typography>
            </Button>
          )}
        </Stack>
        <Grid container rowSpacing={2} columnSpacing={2}>
          {previousDeployments?.map((item) => (
            <DeploymentCard key={item.id} deploymentStatus="inActive" structure={item} />
          ))}
        </Grid>
        <Box
          sx={{
            marginTop: 5,
            borderBottom: 1,
            borderColor: 'divider',
            '& button': { textTransform: 'none', fontWeight: 400 },
            '& .Mui-selected': { fontWeight: 500 },
          }}
        >
          <Tabs value={selectedTabId} onChange={handleTabsChange}>
            {tabsData.map((tab) => (
              <Tab
                key={tab.id}
                label={tab.label}
                sx={{
                  color: abyssColors.primary[500],
                  fontWeight: 600,
                  '&.Mui-selected': {
                    color: abyssColors.primary[700],
                  },
                }}
              />
            ))}
          </Tabs>
        </Box>
        {tabsData.map((tab) => (
          <TabContent key={tab.id} selectedTabId={selectedTabId} id={tab.id}>
            {tab.component}
          </TabContent>
        ))}
      </AssetsContainer>
    </MainContainer>
  );
};
